"use client";
import React, { createContext, useContext, useState, useCallback } from "react";
import { BASE_URL } from "../config/config";

const CourseContext = createContext();

export const CourseProvider = ({ children }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

    // 🔹 FETCH COURSES
    const fetchCourses = useCallback(async () => {
        if (!token) return;
        setLoading(true);

        try {
        const res = await fetch(`${BASE_URL}/courses`, {
            headers: {
            Authorization: `Bearer ${token}`,
            },
        });

        const data = await res.json();

        if (res.ok && data.status === 200) {
            setCourses(data.data || []);
        } else {
            setError(data.message || "Failed to fetch courses");
        }
        } catch {
        setError("Network error");
        } finally {
        setLoading(false);
        }
    }, [token]);

   // 🔹 ADD COURSE
    const addCourse = async (payload) => {
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        payload.append("create_audit_id", user.id);

        // Debugging: log FormData
        for (let pair of payload.entries()) {
        console.log(pair[0], pair[1]);
        }

        const res = await fetch(`${BASE_URL}/courses`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
            // Do NOT set Content-Type for FormData
        },
        body: payload,
        });

        const data = await res.json();

        if (res.ok && (data.status === 200 || data.status === 201)) {
        fetchCourses();
        return true;
        } else {
        console.warn("Add failed:", data.message);
        return false;
        }
    } catch (err) {
        console.error("Network error:", err);
        return false;
    }
    };

    // 🔹 UPDATE COURSE
    const updateCourse = async (id, payload) => {
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        // Ensure payload is FormData
        let formData;
        if (payload instanceof FormData) {
        formData = payload;
        } else {
        formData = new FormData();
        for (const key in payload) {
            if (payload[key] !== undefined && payload[key] !== null) {
            if (key === "image" && payload[key] instanceof File) {
                formData.append(key, payload[key]);
            } else {
                formData.append(key, payload[key]);
            }
            }
        }
        }

        // Always append update_audit_id
        formData.append("update_audit_id", user.id);

        // Debugging: log FormData
        for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
        }

        const res = await fetch(`${BASE_URL}/courses/${id}`, {
        method: "POST", // backend expects POST
        headers: {
            Authorization: `Bearer ${token}`,
            // Do NOT set Content-Type for FormData
        },
        body: formData,
        });

        const data = await res.json();

        if (res.ok && data.status === 200) {
            fetchCourses();
            return true;
        } else {
            console.warn("Update failed:", data.message);
        return false;
        }
        } catch (err) {
            console.error("Network error:", err);
            return false;
        }
    };



    // 🔹 DELETE COURSE
    const deleteCourse = async (id) => {
        if (!window.confirm("Delete this course?")) return;

        try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        const res = await fetch(`${BASE_URL}/courses/${id}`, {
            method: "DELETE",
            headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
            delete_audit_id: user.id,
            }),
        });

        const data = await res.json();

        if (res.ok && data.status === 200) {
            setCourses((prev) => prev.filter((c) => c.id !== id));
        } else {
            alert(data.message || "Delete failed");
        }
        } catch {
        alert("Delete failed");
        }
    };

    // 🔹 STATUS TOGGLE
    const toggleCourseStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

        try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        const res = await fetch(`${BASE_URL}/courses/${id}/status`, {
            method: "PATCH",
            headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
            status: newStatus,
            update_audit_id: user.id,
            }),
        });

        const data = await res.json();

        if (res.ok && data.status === 200) {
            setCourses((prev) =>
            prev.map((c) =>
                c.id === id ? { ...c, status: newStatus } : c
            )
            );
        } else {
            alert(data.message || "Status update failed");
        }
        } catch {
        alert("Status update failed");
        }
    };

    return (
        <CourseContext.Provider
        value={{
            courses,
            loading,
            error,
            fetchCourses,
            addCourse,
            updateCourse,
            deleteCourse,
            toggleCourseStatus,
        }}
        >
        {children}
        </CourseContext.Provider>
    );
};

export const useCourses = () => useContext(CourseContext);
